import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { VARIANT, VIP_ACQUISTA_URL, VIP_LOGIN_URL, VIP_URL } from "@/constants/links";
import { track } from "@/lib/tracker";

const LINKS =
  VARIANT === "vip"
    ? [
        // 2026-10-09: parità con la nav live (Funzioni/Prezzi/Installazione/
        // Assistenza → pagine SEO dedicate) + nuova pagina WhatsApp.
        { label: "Funzioni", href: "#funzioni" },
        { label: "WhatsApp", href: "/funzioni/whatsapp/" },
        { label: "Demo", href: "#demo" },
        { label: "Prezzi", href: "/prezzi/" },
        { label: "Installazione", href: "/installazione/" },
        { label: "Assistenza", href: "/assistenza/" },
      ]
    : [
        { label: "Come funziona", href: "#come-funziona" },
        { label: "Funzioni", href: "#funzioni" },
        { label: "Confronta", href: "#confronta" },
        { label: "FAQ", href: "#domande-frequenti" },
        { label: "Pannello Genitore", href: "#pannello" },
        { label: "Privacy", href: "#privacy" },
      ];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isVip = VARIANT === "vip";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-ink/10 bg-white/90 shadow-sm backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8 md:h-20">
        <a href="/" aria-label="Guardian Family, pagina iniziale" data-testid="nav-logo" className="flex items-center">
          <img
            src="/logo-256.webp"
            alt="Guardian Family"
            width="256"
            height="256"
            className="h-14 w-auto md:h-16"
            data-testid="nav-logo-image"
          />
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm font-semibold text-bodytext transition-colors hover:text-ink"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          {isVip ? (
            <>
              <a
                data-testid="nav-acquista-link"
                href={VIP_ACQUISTA_URL}
                onClick={() => track("acquista_click")}
                className="rounded-full border-2 border-violet px-5 py-2.5 text-sm font-bold text-violet transition-all hover:-translate-y-0.5 hover:bg-softpurple"
              >
                Acquista VIP
              </a>
              <a
                data-testid="nav-login-cta"
                href={VIP_LOGIN_URL}
                onClick={() => track("login_click")}
                className="btn-3d-yellow rounded-full px-5 py-2.5 text-sm font-extrabold"
              >
                Accedi alla dashboard
              </a>
            </>
          ) : (
            <>
              <a
                data-testid="nav-login-link"
                href="/login"
                onClick={() => track("login_click")}
                className="rounded-full border-2 border-ink/15 px-5 py-2.5 text-sm font-bold text-ink transition-all hover:-translate-y-0.5 hover:bg-white"
              >
                Accedi
              </a>
              <a
                data-testid="nav-vip-link"
                href={VIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("vip_link_click")}
                className="rounded-full border-2 border-violet px-5 py-2.5 text-sm font-bold text-violet transition-all hover:-translate-y-0.5 hover:bg-softpurple"
              >
                VIP (prodotto separato)
              </a>
              <a
                data-testid="nav-download-cta"
                href="#download"
                className="btn-3d-yellow rounded-full px-5 py-2.5 text-sm font-extrabold"
              >
                Scarica da Google Play
              </a>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-button"
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md text-ink lg:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-b border-ink/10 bg-white/95 backdrop-blur-md lg:hidden"
          >
            <div className="space-y-1 px-5 pb-6 pt-2 sm:px-8">
              {LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-2xl px-3 py-3 text-base font-semibold text-ink hover:bg-softpurple"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-4">
                {isVip ? (
                  <>
                    <a
                      data-testid="mobile-acquista-link"
                      href={VIP_ACQUISTA_URL}
                      onClick={() => track("acquista_click")}
                      className="rounded-full border-2 border-violet px-5 py-3 text-center text-sm font-bold text-violet"
                    >
                      Acquista VIP
                    </a>
                    <a
                      data-testid="mobile-login-cta"
                      href={VIP_LOGIN_URL}
                      onClick={() => track("login_click")}
                      className="btn-3d-yellow rounded-full px-5 py-3 text-center text-sm font-extrabold"
                    >
                      Accedi alla dashboard
                    </a>
                  </>
                ) : (
                  <>
                    <a
                      data-testid="mobile-login-link"
                      href="/login"
                      className="rounded-full border-2 border-ink/15 px-5 py-3 text-center text-sm font-bold text-ink"
                    >
                      Accedi
                    </a>
                    <a
                      data-testid="mobile-vip-link"
                      href={VIP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("vip_link_click")}
                      className="rounded-full border-2 border-violet px-5 py-3 text-center text-sm font-bold text-violet"
                    >
                      VIP (prodotto separato)
                    </a>
                    <a
                      data-testid="mobile-download-cta"
                      href="#download"
                      onClick={() => setOpen(false)}
                      className="btn-3d-yellow rounded-full px-5 py-3 text-center text-sm font-extrabold"
                    >
                      Scarica da Google Play
                    </a>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
